import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { getPermissionMatrix, updateRolePermissions } from '@/api/admin-permissions';
import type { AdminPermission, AdminRole, PermissionMatrix } from '@/types/admin-permission';
import { Shield, Loader2, Check, AlertCircle, ChevronRight, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Permission groups in sidebar order
const PERMISSION_GROUPS: { key: string; label: string; prefixes: string[] }[] = [
  { key: 'schools', label: 'Écoles', prefixes: ['sidebar.schools', 'action.schools'] },
  { key: 'deployments', label: 'Deploiements', prefixes: ['action.deployments'] },
  { key: 'school_networks', label: 'Reseaux', prefixes: ['sidebar.school_networks', 'action.school_networks'] },
  { key: 'v2', label: 'V2 / Reseaux V2', prefixes: ['sidebar.v2_tenant_groups', 'action.v2_groups', 'action.v2.'] },
  { key: 'white_labels', label: 'Marques blanches', prefixes: ['sidebar.white_labels', 'action.white_labels'] },
  { key: 'modules', label: 'Modules', prefixes: ['sidebar.modules', 'action.modules'] },
  { key: 'school_years', label: 'Années fiscales', prefixes: ['sidebar.school_years', 'action.school_years'] },
  { key: 'permissions', label: 'Permissions & Roles', prefixes: ['sidebar.permissions', 'sidebar.roles', 'sidebar.admin_permissions', 'action.permissions'] },
  { key: 'migrations', label: 'Migrations', prefixes: ['sidebar.migrations', 'action.migrations'] },
  { key: 'databases', label: 'Bases de donnees', prefixes: ['sidebar.databases', 'action.databases'] },
  { key: 'backups', label: 'Sauvegardes', prefixes: ['action.backups'] },
  { key: 'exports', label: 'Exports', prefixes: ['sidebar.exports', 'action.exports'] },
  { key: 'notifications', label: 'Notifications', prefixes: ['sidebar.notifications'] },
  { key: 'search', label: 'Recherches', prefixes: ['sidebar.search'] },
  { key: 'scripts', label: 'Scripts', prefixes: ['sidebar.scripts'] },
  { key: 'login_photos', label: 'Photos du login', prefixes: ['sidebar.login_photos', 'action.login_photos'] },
  { key: 'accounts', label: 'Comptes', prefixes: ['sidebar.accounts', 'action.accounts'] },
  { key: 'administrators', label: 'Administrateurs', prefixes: ['sidebar.administrators'] },
  { key: 'other', label: 'Autres', prefixes: [] },
];

// Get group for a permission code
function getPermissionGroup(code: string): string {
  for (const group of PERMISSION_GROUPS) {
    if (group.prefixes.some((prefix) => code.startsWith(prefix))) {
      return group.key;
    }
  }
  return 'other';
}

// Get group label
function getGroupLabel(key: string): string {
  const group = PERMISSION_GROUPS.find((g) => g.key === key);
  return group?.label || key;
}

// Role badge colors
function getRoleBadgeVariant(roleCode: string): 'destructive' | 'warning' | 'success' | 'secondary' | 'outline' {
  switch (roleCode) {
    case 'ROLE_SUPERADMIN':
      return 'destructive';
    case 'ROLE_ADMIN':
      return 'warning';
    case 'ROLE_USER':
      return 'success';
    case 'ROLE_RETAILER':
      return 'success';
    case 'ROLE_DEV':
      return 'secondary';
    case 'ROLE_SUPPORT':
      return 'outline';
    case 'ROLE_CONFIG':
      return 'secondary';
    default:
      return 'outline';
  }
}

export function AdminPermissionsPage() {
  const [matrix, setMatrix] = useState<PermissionMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingRole, setSavingRole] = useState<string | null>(null);
  const [savedRole, setSavedRole] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Toggle group expansion
  const toggleGroup = useCallback((group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }, []);

  // Check if group is expanded
  const isGroupExpanded = useCallback((group: string) => {
    return expandedGroups.has(group);
  }, [expandedGroups]);

  // Load permission matrix
  useEffect(() => {
    async function loadMatrix() {
      try {
        setLoading(true);
        const data = await getPermissionMatrix();
        setMatrix(data);
        setError(null);
      } catch (err) {
        console.error('Error loading permission matrix:', err);
        setError('Erreur lors du chargement des permissions');
      } finally {
        setLoading(false);
      }
    }
    loadMatrix();
  }, []);

  // Filter and group permissions by functional group (following sidebar order)
  const permissionsByGroup = useMemo(() => {
    if (!matrix) return {};

    const query = searchQuery.toLowerCase().trim();
    const grouped: Record<string, AdminPermission[]> = {};

    for (const permission of matrix.permissions) {
      // Filter by search query
      if (query && !permission.name.toLowerCase().includes(query) && !permission.code.toLowerCase().includes(query)) {
        continue;
      }

      const group = getPermissionGroup(permission.code);
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(permission);
    }

    // Sort permissions within each group: sidebar first, then actions, then by name
    for (const group of Object.keys(grouped)) {
      grouped[group].sort((a, b) => {
        const aIsSidebar = a.code.startsWith('sidebar.');
        const bIsSidebar = b.code.startsWith('sidebar.');
        if (aIsSidebar && !bIsSidebar) return -1;
        if (!aIsSidebar && bIsSidebar) return 1;
        return a.name.localeCompare(b.name);
      });
    }

    return grouped;
  }, [matrix, searchQuery]);

  // Auto-expand groups when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      setExpandedGroups(new Set(Object.keys(permissionsByGroup)));
    } else {
      setExpandedGroups(new Set());
    }
  }, [searchQuery, permissionsByGroup]);

  // Get sorted group keys (following PERMISSION_GROUPS order)
  const sortedGroups = useMemo(() => {
    const groupOrder = PERMISSION_GROUPS.map((g) => g.key);
    return Object.keys(permissionsByGroup).sort((a, b) => {
      const orderA = groupOrder.indexOf(a);
      const orderB = groupOrder.indexOf(b);
      return (orderA === -1 ? 99 : orderA) - (orderB === -1 ? 99 : orderB);
    });
  }, [permissionsByGroup]);

  // Handle permission toggle
  const handleToggle = useCallback(
    async (role: AdminRole, permissionCode: string, checked: boolean) => {
      if (!matrix) return;

      // Update local state optimistically
      const newPermissions = checked
        ? [...role.permissions, permissionCode]
        : role.permissions.filter((p) => p !== permissionCode);

      setMatrix((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          roles: prev.roles.map((r) =>
            r.code === role.code ? { ...r, permissions: newPermissions } : r
          ),
        };
      });

      // Save to server
      setSavingRole(role.code);
      try {
        await updateRolePermissions(role.code, newPermissions);
        setSavedRole(role.code);
        setTimeout(() => setSavedRole(null), 1500);
      } catch (err) {
        console.error('Error updating permissions:', err);
        // Revert on error
        setMatrix((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            roles: prev.roles.map((r) =>
              r.code === role.code ? { ...r, permissions: role.permissions } : r
            ),
          };
        });
      } finally {
        setSavingRole(null);
      }
    },
    [matrix]
  );

  // Check if a role has a permission
  const hasPermission = useCallback(
    (role: AdminRole, permissionCode: string) => {
      return role.permissions.includes(permissionCode);
    },
    []
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  if (!matrix) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Shield className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Permissions Admin
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Configurez les permissions par role d'administrateur
          </p>
        </div>
      </div>

      {/* Permission Matrix */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg">Matrice des permissions</CardTitle>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Rechercher une permission..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9 h-9"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-background z-10">
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground min-w-[200px]">
                    Permission
                  </th>
                  {matrix.roles.map((role) => (
                    <th
                      key={role.code}
                      className="text-center py-3 px-2 font-medium min-w-[100px]"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <Badge variant={getRoleBadgeVariant(role.code)} className="text-xs whitespace-nowrap">
                          {role.name}
                        </Badge>
                        {savingRole === role.code && (
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        )}
                        {savedRole === role.code && (
                          <Check className="h-3 w-3 text-green-500" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedGroups.map((group) => (
                  <>
                    {/* Group header - clickable */}
                    <tr
                      key={`group-${group}`}
                      className="bg-primary/10 border-t-2 border-primary/30 cursor-pointer hover:bg-primary/20 transition-colors"
                      onClick={() => toggleGroup(group)}
                    >
                      <td
                        colSpan={matrix.roles.length + 1}
                        className="py-3 px-4"
                      >
                        <div className="flex items-center gap-2">
                          <ChevronRight
                            className={cn(
                              'h-4 w-4 text-primary transition-transform',
                              isGroupExpanded(group) && 'rotate-90'
                            )}
                          />
                          <span className="font-bold text-sm text-primary uppercase tracking-wider">
                            {getGroupLabel(group)}
                          </span>
                          <span className="text-xs text-primary/60 font-normal normal-case">
                            ({permissionsByGroup[group].length} permissions)
                          </span>
                        </div>
                      </td>
                    </tr>
                    {/* Role reminder row when group is expanded */}
                    {isGroupExpanded(group) && (
                      <tr key={`roles-${group}`} className="bg-muted/30 border-b">
                        <td className="py-2 px-4 pl-10 text-xs text-muted-foreground">
                          Permission
                        </td>
                        {matrix.roles.map((role) => (
                          <td key={`role-reminder-${group}-${role.code}`} className="text-center py-2 px-2">
                            <span className="text-[10px] text-muted-foreground font-medium">
                              {role.name.split(' ')[0]}
                            </span>
                          </td>
                        ))}
                      </tr>
                    )}
                    {/* Permissions in this group - collapsible */}
                    {isGroupExpanded(group) && permissionsByGroup[group].map((permission) => (
                      <tr
                        key={permission.code}
                        className="border-b hover:bg-muted/30 transition-colors"
                      >
                        <td className="py-3 px-4 pl-10">
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{permission.name}</p>
                              <p className="text-xs text-muted-foreground">{permission.code}</p>
                            </div>
                            {permission.code.startsWith('sidebar.') && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                                Menu
                              </span>
                            )}
                          </div>
                        </td>
                        {matrix.roles.map((role) => (
                          <td key={`${permission.code}-${role.code}`} className="text-center py-3 px-2">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={hasPermission(role, permission.code)}
                                onCheckedChange={(checked) =>
                                  handleToggle(role, permission.code, checked === true)
                                }
                                disabled={savingRole === role.code}
                                className={cn(
                                  'transition-opacity',
                                  savingRole === role.code && 'opacity-50'
                                )}
                              />
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">
              Roles :
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {matrix.roles.map((role) => (
                <div key={role.code} className="flex items-center gap-2 p-2 rounded-md bg-muted/30">
                  <Badge variant={getRoleBadgeVariant(role.code)} className="text-xs">
                    {role.name}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {role.permissions.length} permissions
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
